import { SignUpForm, SignUpFormTheme, SignUpFormField } from '@masonic-tools/shared-ui';
import { useAuth } from '@/contexts/AuthContext';
import { showError } from '@/lib/swal';
import { validateEmail, validatePassword } from '@masonic-tools/shared-auth';

const secretaryTheme: SignUpFormTheme = {
  appName: 'Secretary',
  appNameColor: 'text-black',
  title: 'Create Your Account',
  description: 'Join the distinguished platform for unit record-keeping',
  loginPath: '/login',
  termsPath: '/terms',
  privacyPath: '/privacy',
  successRedirectPath: '/dashboard',
};

export default function SignUpPage() {
  const { signup } = useAuth();

  const signUpFields: SignUpFormField[] = [
    {
      name: 'name',
      label: 'Full Name',
      type: 'text',
      placeholder: 'John Smith',
      required: true,
    },
    {
      name: 'email',
      label: 'Email Address',
      type: 'email',
      placeholder: 'john@example.com',
      required: true,
    },
    {
      name: 'unitName',
      label: 'Unit Name',
      type: 'text',
      placeholder: 'St. John\'s Unit',
      required: true,
    },
    {
      name: 'unitNumber',
      label: 'Unit Number',
      type: 'text',
      placeholder: '123',
      required: true,
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      placeholder: '••••••••',
      required: true,
      helpText: 'At least 8 characters, 1 uppercase letter, and 1 number',
    },
    {
      name: 'confirmPassword',
      label: 'Confirm Password',
      type: 'password',
      placeholder: '••••••••',
      required: true,
    },
  ];

  const validateForm = (data: Record<string, any>) => {
    if (!data.name?.trim()) {
      return { isValid: false, error: 'Please enter your name' };
    }

    if (!validateEmail(data.email)) {
      return { isValid: false, error: 'Please enter a valid email address' };
    }

    if (!data.unitName?.trim()) {
      return { isValid: false, error: 'Please enter your unit name' };
    }

    if (!data.unitNumber?.trim()) {
      return { isValid: false, error: 'Please enter your unit number' };
    }

    const passwordValidation = validatePassword(data.password);
    if (!passwordValidation.isValid) {
      return { isValid: false, error: passwordValidation.errors.join('. ') };
    }

    if (data.password !== data.confirmPassword) {
      return { isValid: false, error: 'Passwords do not match' };
    }

    return { isValid: true };
  };

  const handleSignUp = async (data: Record<string, any>) => {
    const { email, password, confirmPassword, ...additionalData } = data;
    await signup(email, password, additionalData);
  };

  return (
    <SignUpForm
      theme={secretaryTheme}
      fields={signUpFields}
      onSignUp={handleSignUp}
      onError={showError}
      validateForm={validateForm}
    />
  );
}

